import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### 他事業所連携メッセージ API 操作クラス
 */
export default class OtherOfficeMessages {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET OtherOfficeMessages
   */
  public async gets(userStore: LoginUser, otherOfficeFacilityId: number, queryParams?: { startIndex: number; endIndex: number }): Promise<Array<OtherOfficeMessageGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      otherOfficeFacilityId: otherOfficeFacilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeMessages, formatParams);
    const params: any = queryParams ? queryParams : {};

    const response = await this.commonService.executeGet<Array<OtherOfficeMessageGetModel>>(
      userStore,
      requestUrl,
      this.constructor.name,
      params,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * ### GET OtherOfficeMessages
   */
  public async getCount(userStore: LoginUser, otherOfficeFacilityId: number): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      otherOfficeFacilityId: otherOfficeFacilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeMessages, formatParams);

    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * ### POST OtherOfficeMessage
   */
  public async post(userStore: LoginUser, otherOfficeFacilityId: number, requestBody: OtherOfficeMessagePostModel): Promise<OtherOfficeMessageGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      otherOfficeFacilityId: otherOfficeFacilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeMessages, formatParams);

    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH OtherOfficeMessage
   */
  public async patch(userStore: LoginUser, otherOfficeFacilityId: number, datetime: number, requestBody: OtherOfficeMessagePatchModel): Promise<OtherOfficeMessageGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      otherOfficeFacilityId: otherOfficeFacilityId,
      datetime: datetime
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeMessage, formatParams);

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE OtherOfficeMessage
   */
  public async delete(userStore: LoginUser, otherOfficeFacilityId: number, datetime: number): Promise<OtherOfficeMessageGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      otherOfficeFacilityId: otherOfficeFacilityId,
      datetime: datetime
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeMessage, formatParams);

    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface OtherOfficeMessageGetModel {
  /** otherOfficeMessageKeysTBLで採番した一意のID */
  id: number;
  /** 送信（投稿）日時 */
  datetime: number;
  /** 最終送信・返信日時。ソートで使用する */
  latestReplyDatetime: number;
  /** 投稿した施設ID */
  senderFacilityId: number;
  /** タイトル */
  title: string;
  /** 本文 */
  text: string;
  /** 添付ファイルのS3URL */
  fileUrls: Array<FileUrl>;
  /** 送信（投稿）したユーザーID。一覧取得時、他施設の場合はマスクする */
  userId: string;
  /** ユーザー名。一覧取得時、他施設の場合はマスクする（v3.38.0以前は未格納） */
  userName?: string;
  /** 未読施設IDのMAP */
  unreadFacilities: Array<number>;
  /** 返信数 */
  replyCount: number;

  /** 削除フラグ */
  deleteFlag: boolean;
  /** 作成日時 */
  created: number;
  /** 更新日時 */
  updated: number;
}

export interface FileUrl {
  url: string;
  name: string;
}

export interface OtherOfficeMessagePostModel {
  /** タイトル */
  title: string;
  /** 本文 */
  text: string;
  /** 添付ファイルのS3URL */
  fileUrls: Array<FileUrl>;
}

export interface OtherOfficeMessagePatchModel {
  /** 更新種別 */
  updateType: UpdateType;
  /** タイトル */
  title: string | undefined;
  /** 本文 */
  text: string | undefined;
  /** 添付ファイルのS3URL */
  fileUrls: Array<FileUrl> | undefined;
  /** 更新日時 */
  updated: number;
}

export enum UpdateType {
  EDIT = "edit",
  UNDO = "undo",
  READ = "read"
}
